// SPDX-License-Identifier: AGPL-3.0-or-later
import type { Channel } from './types';

const NF = new Intl.NumberFormat('en-AU');

export function num(n: number | null | undefined, dp = 0): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  return dp > 0
    ? n.toLocaleString('en-AU', { minimumFractionDigits: dp, maximumFractionDigits: dp })
    : NF.format(Math.round(n));
}

export function rate(n: number | null | undefined, dp = 1): string {
  return n === null || n === undefined || !Number.isFinite(n) ? '—' : n.toFixed(dp);
}

export function pct(n: number | null | undefined, dp = 1): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  return `${(n * 100).toFixed(dp)}%`;
}

/** Signed percentage, for change columns. */
export function delta(n: number | null | undefined, dp = 0): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  const s = (n * 100).toFixed(dp);
  return `${n > 0 ? '+' : ''}${s}%`;
}

/** Compact "people" count — 4.4m, 340k, 1,200. */
export function people(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  const a = Math.abs(n);
  if (a >= 1e6) return `${(n / 1e6).toFixed(1)}m`;
  if (a >= 1e4) return `${(n / 1e3).toFixed(0)}k`;
  return NF.format(Math.round(n));
}

/** Escape text destined for innerHTML. */
export function esc(s: unknown): string {
  return String(s ?? '').replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string,
  );
}
/** Escape for a data-tip attribute (rendered as textContent — keep plain). */
export const tip = (s: string): string => esc(s);

/* ── channels: one colour + label everywhere ── */
export const CHANNELS: Channel[] = ['branch', 'bankpost', 'other', 'atm'];
export const CHANNEL_LABEL: Record<Channel, string> = {
  branch: 'Branch',
  bankpost: 'Bank@Post',
  other: 'Other face-to-face',
  atm: 'ATM',
};
export const CHANNEL_COLOUR: Record<Channel, string> = {
  branch: '#1e3a5f', // vault navy
  bankpost: '#0f766e', // Australia Post teal
  other: '#b06a1f', // amber-brown
  atm: '#7a8699', // steel grey
};

export const LOST_COLOUR = '#b3261e';

/* ── states ── */
export const STATE_ORDER = ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'];
export const STATE_FULL: Record<string, string> = {
  NSW: 'New South Wales', VIC: 'Victoria', QLD: 'Queensland', SA: 'South Australia',
  WA: 'Western Australia', TAS: 'Tasmania', NT: 'Northern Territory', ACT: 'Australian Capital Territory',
  Other: 'Other territories', 'Offshore areas': 'Offshore areas',
};
export const STATE_COLOUR: Record<string, string> = {
  NSW: '#3d6fb4', VIC: '#5a4b9c', QLD: '#b5642f', SA: '#b03a68',
  WA: '#c79a33', TAS: '#2f8f6b', NT: '#c85a3a', ACT: '#4f8a9c',
};
export const stateColour = (s: string): string => STATE_COLOUR[s] ?? 'var(--text-tertiary)';

/* ── remoteness ── */
export const REMOTENESS_ORDER = [
  'Major Cities of Australia',
  'Inner Regional Australia',
  'Outer Regional Australia',
  'Remote Australia',
  'Very Remote Australia',
];
export const REMOTENESS_SHORT: Record<string, string> = {
  'Major Cities of Australia': 'Major Cities',
  'Inner Regional Australia': 'Inner Regional',
  'Outer Regional Australia': 'Outer Regional',
  'Remote Australia': 'Remote',
  'Very Remote Australia': 'Very Remote',
};
// Pale → deep as remoteness increases (used for the equity ramp).
export const REMOTENESS_COLOUR: Record<string, string> = {
  'Major Cities of Australia': '#94b3cf',
  'Inner Regional Australia': '#6f97bd',
  'Outer Regional Australia': '#c98a3a',
  'Remote Australia': '#b3623a',
  'Very Remote Australia': '#8f3f2e',
};

/** Access-ramp: MORE branches per person = deeper (good), for the choropleth. */
export function accessRamp(value: number | null, thresholds: number[]): string {
  const ramp = ['var(--sev-1)', 'var(--sev-2)', 'var(--sev-3)', 'var(--sev-4)', 'var(--sev-5)'];
  if (value === null || !Number.isFinite(value)) return 'var(--no-data)';
  let i = 0;
  while (i < thresholds.length && value >= thresholds[i]) i++;
  return ramp[i];
}
